import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { colors, typography } from '../../styles/theme';
import {
  APP_MODAL_DIVIDER,
  APP_MODAL_TEXT,
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalContentSx,
  appModalFieldSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalSecondaryButtonSx,
  appModalSubtitleSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../appModalChrome.styles';

/*
 * Piezas del «HISTORIAL» (salieron de `CollectionActivityDialog`, el historial del detalle de una
 * lista) para que otros historiales (Meta: «cambios desde Terrio») usen la misma estructura y estilos:
 * título con ✕, línea de contexto, buscador + selectores, grupos por día, tarjetas con ícono en
 * círculo, actor con su rol, fecha a la derecha, desplegable con el detalle y «Cerrar» abajo.
 */

const skRowSx = { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '8px' };

export const activityHistorySelectSx = {
  minWidth: 148,
  height: 40,
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: typography.fontFamily.primary,
  color: APP_MODAL_TEXT,
  bgcolor: colors.backgroundSurface,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: APP_MODAL_DIVIDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: APP_MODAL_DIVIDER },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
  '& .MuiSelect-icon': { color: colors.textSecondary },
  '& .MuiSelect-select': { py: '10px', px: '12px' },
};

export const activityHistoryMenuPaperSx = {
  bgcolor: colors.backgroundSurface,
  backgroundImage: 'none',
  border: `1px solid ${APP_MODAL_DIVIDER}`,
};

/** Scroll usable, sin barra (mismo criterio que el panel de filtros). */
export const activityHistoryHiddenScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { width: 0, height: 0, display: 'none' },
};

const menuItemSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '14px',
  color: APP_MODAL_TEXT,
};

export function ActivityHistorySkeleton({ count = 4 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} aria-hidden>
      {Array.from({ length: count }, (_, key) => (
        <Box
          key={key}
          sx={{
            display: 'flex',
            gap: 1.25,
            minHeight: 88,
            px: 1.25,
            py: 1.25,
            borderRadius: '8px',
            bgcolor: colors.backgroundDark,
          }}
        >
          <Skeleton variant="circular" width={32} height={32} sx={skRowSx} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="48%" height={16} sx={skRowSx} />
            <Skeleton variant="text" width="62%" height={12} sx={skRowSx} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Selector del buscador (`options`: `{ id, label }`). */
export function ActivityHistorySelect({ value, onChange, options, ariaLabel, sx = null, testId }) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={ariaLabel}
      inputProps={{ 'aria-label': ariaLabel }}
      sx={{ ...activityHistorySelectSx, flexShrink: 0, ...(sx || {}) }}
      MenuProps={{ PaperProps: { sx: activityHistoryMenuPaperSx } }}
      data-testid={testId}
      displayEmpty
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id} sx={menuItemSx}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}

/** Buscador + selectores (`children`). */
export function ActivityHistoryFilterBar({
  query,
  onQueryChange,
  placeholder,
  children,
  testId,
}) {
  return (
    <Box
      role="search"
      aria-label="Filtrar el historial"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      <TextField
        size="small"
        placeholder={placeholder}
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        sx={{ ...appModalFieldSx, flex: 1, minWidth: 0 }}
        inputProps={{ maxLength: 80, 'aria-label': 'Buscar en el historial', 'data-testid': testId }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
            </InputAdornment>
          ),
        }}
      />
      {children}
    </Box>
  );
}

/** Grupo de un día («VIERNES, 23 OCT»). */
export function ActivityHistoryDayGroup({ label, children, testId }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }} data-testid={testId}>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
          px: 0.25,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Tarjeta de un evento. `details`: `{ key, text }[]` (lo que abre el desplegable `summary`);
 * `badge`: algo al lado del título (p. ej. un chip de resultado); `onOpen`: la tarjeta entera abre algo.
 */
export function ActivityHistoryCard({
  icon: Icon = HistoryOutlinedIcon,
  title,
  badge = null,
  time = '',
  actor = '',
  roleKey = '',
  roleLabel = '',
  summary = '',
  details = [],
  expanded = false,
  onToggle,
  onOpen = null,
  testId,
  dataAttrs = null,
}) {
  const hasDetails = details.length > 0;
  const canOpen = typeof onOpen === 'function';
  return (
    <Box
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={canOpen ? onOpen : undefined}
      onKeyDown={canOpen ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      } : undefined}
      data-testid={testId}
      {...(dataAttrs || {})}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: 1.25,
        py: 1.25,
        borderRadius: '8px',
        bgcolor: colors.backgroundDark,
        cursor: canOpen ? 'pointer' : 'default',
        ...(canOpen ? {
          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
        } : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minHeight: 64,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.06)',
            color: colors.textPrimary,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: '13px',
                lineHeight: '18px',
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.primary,
                fontWeight: 600,
                overflowWrap: 'anywhere',
              }}
              data-testid={testId ? `${testId}-title` : undefined}
            >
              {title}
            </Typography>
            {badge}
            {time ? (
              <Typography
                sx={{
                  fontSize: '11px',
                  lineHeight: '16px',
                  color: colors.textSecondary,
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {time}
              </Typography>
            ) : null}
          </Box>
          {(actor || roleLabel) ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.35,
                minWidth: 0,
              }}
            >
              {actor ? (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: colors.textSecondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {actor}
                </Typography>
              ) : null}
              {actor && roleLabel ? (
                <Typography sx={{ fontSize: '12px', color: colors.textSecondary, flexShrink: 0 }}>
                  ·
                </Typography>
              ) : null}
              {roleLabel ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {roleKey === 'owner' ? (
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: colors.primary,
                      }}
                    />
                  ) : null}
                  <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>
                    {roleLabel}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
          {hasDetails ? (
            <Button
              onClick={(event) => {
                event.stopPropagation();
                onToggle?.();
              }}
              aria-expanded={expanded}
              endIcon={(
                <ExpandMoreIcon
                  sx={{
                    fontSize: 18,
                    color: colors.textSecondary,
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 120ms ease',
                  }}
                />
              )}
              sx={{
                textTransform: 'none',
                minWidth: 0,
                px: 0,
                py: 0,
                mt: 0.5,
                minHeight: 24,
                justifyContent: 'flex-start',
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: typography.fontFamily.primary,
                color: colors.textSecondary,
                '& .MuiButton-endIcon': { ml: 0.5 },
                '&:hover': { bgcolor: 'transparent', color: colors.textPrimary },
              }}
              data-testid={testId ? `${testId}-toggle` : undefined}
            >
              {summary}
            </Button>
          ) : null}
        </Box>
      </Box>
      {hasDetails ? (
        <Box sx={{ display: 'flex', gap: 1.25 }}>
          <Box sx={{ width: 32, flexShrink: 0 }} aria-hidden />
          <Collapse in={expanded} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, pt: 0.25 }}>
              {details.map((detail) => (
                <Typography
                  key={detail.key}
                  sx={{ fontSize: '12px', color: colors.textSecondary, overflowWrap: 'anywhere' }}
                >
                  {detail.text}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}

/** Estado vacío / error centrado en la lista, con una acción opcional. */
export function ActivityHistoryEmpty({ message, actionLabel = '', onAction = null, testId }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 1.25,
        px: 2,
        textAlign: 'center',
      }}
      data-testid={testId}
    >
      <Typography sx={{ color: colors.textSecondary, fontSize: '13px' }}>
        {message}
      </Typography>
      {actionLabel && onAction ? (
        <Button variant="outlined" onClick={onAction} sx={appModalSecondaryButtonSx}>
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}

/**
 * Marco del diálogo: HISTORIAL + ✕, línea de contexto, `filters`, `error` y la lista (`listRef`,
 * `listSx` para el fade de scroll), y «Cerrar» abajo.
 */
export function ActivityHistoryDialog({
  open,
  onClose,
  subtitle,
  filters = null,
  error = '',
  listRef = null,
  listSx = null,
  busy = false,
  children,
  paperTestId,
  listTestId,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: appModalPaperSx, ...(paperTestId ? { 'data-testid': paperTestId } : {}) }}
    >
      {/* `div`: el título es el h2 de adentro (sin h2 dentro de h2). */}
      <DialogTitle component="div" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>HISTORIAL</Typography>
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={appModalCloseButtonSx}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <Typography sx={appModalSubtitleSx}>
        {subtitle}
      </Typography>
      <DialogContent sx={{ ...appModalContentSx, gap: 1.25 }}>
        {filters}
        {error ? (
          <Typography sx={{ color: colors.error, fontSize: '12px', flexShrink: 0 }}>
            {error}
          </Typography>
        ) : null}
        <Box
          ref={listRef}
          aria-busy={busy || undefined}
          data-testid={listTestId}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            ...activityHistoryHiddenScrollbarSx,
            ...(listSx || {}),
          }}
        >
          {children}
        </Box>
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button variant="outlined" onClick={onClose} sx={appModalSecondaryButtonSx}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
